/**
 * Pure helpers for the link-in-bio page (`/links`, `pages/BioLinksPage.tsx`): which URL each control
 * opens, and how the loader's flat list becomes the page's two views. No React, no I/O, so every rule
 * is unit-testable and the server render and the client's hydrating render cannot disagree: both call
 * these with the same `channel` and `entries` from the SSR payload.
 *
 * Spec: stippl-knowledge `work/stippl-links-page.md`, "Links and tagging". The link shapes are the
 * prototype's `LINKS` object, unchanged:
 *   - app buttons   https://stippl.onelink.me/cgUq/?pid=linktree&c=links_<channel>&af_ios_store_cpp=<Link in bio page>
 *       The slash after `cgUq` is load-bearing: iOS only opens the app from a OneLink whose path
 *       matches AppsFlyer's AASA (`/cgUq/*`); the bare `/cgUq?…` sends an installed user to the
 *       App Store instead (see `withUniversalLinkPath` in components/common/useAppsflyerOneLink.ts).
 *       `pid` stays `linktree` so the install row in the ROAS report runs on across the switch from
 *       Linktree.com; `c` is what splits Instagram from TikTok (it lands in `acquisition.campaign`).
 *   - web links     https://www.stippl.io/<path>?utm_source=<channel>&utm_medium=social&utm_campaign=bio_links
 *   - trip links    https://www.stippl.io/routepreview/<tripId>?utm_source=<channel>&utm_medium=social&utm_campaign=<asked_|video_><slug>
 *   - guide links   https://www.stippl.io/listpreview/<listId>?utm_source=<channel>&utm_medium=social&utm_campaign=<asked_|video_><slug>
 *   - DM card       https://ig.me/m/stippl.io (the same for both channels: TikTok has no DM link)
 *
 * And the list rules of the page (the 7 Oct review): the newest 7 leave out viral routes, "Viral trips"
 * shows the viral ones by `rank` three at a time, and every route still sits in its country folder.
 *
 * A deal (`kind: "deal"`: a time-boxed highlighted button with its own `url`) is in the same list but is
 * none of those: it has no country, photo or trip, so every list helper below leaves it out and
 * `dealEntries` is the only way to get at it.
 */

import { withCustomProductPage } from "components/common/customProductPage";

export const BIO_LINK_CHANNELS = ["instagram", "tiktok"] as const;
export type BioLinkChannel = (typeof BIO_LINK_CHANNELS)[number];

/** What a tap on the page was, for `links_click`. */
export type BioLinkKind = "app" | "trip" | "guide" | "video" | "dm" | "discover" | "esim" | "social" | "home" | "deal";

export type BioLinkSource = "asked" | "video";

/** One live entry, exactly as `loadBioLinks` (graphql/helpers/marketingSsrLoaders.ts) hands it over. */
export interface BioLinkEntry {
	slug: string;
	/** Absent on payloads from before deals existed. A deal carries only `slug`, `url`, `cta`, `title` and `meta`: no country, photo or trip. */
	kind?: "route" | "guide" | "deal";
	/** A deal's target; the button opens it as it is. */
	url?: string | null;
	/** A deal's button label; null or absent reads "Open the app". */
	cta?: string | null;
	/** A route's trip id. Exactly one of tripId and listId is set; the loader sends null for the other. */
	tripId?: string | null;
	/** A guide's list id: the card opens `/listpreview/<listId>` instead of a trip. */
	listId?: string | null;
	/** Folder key (an `explore_countries` slug); also the folder's DOM id, so `#japan` can open it. */
	country: string;
	countryName: string;
	flag: string;
	title: string;
	meta: string;
	/** null for a guide whose places have no licensed photo: the card renders without one. */
	image: string | null;
	imageAlt: string;
	imageCredit: string;
	/** Bookkeeping only since the 7 Oct review: every card's chip reads "From our video" whatever this says. */
	source: BioLinkSource;
	/** In "Viral trips" instead of the newest 7. Still in its country folder. */
	viral?: boolean;
	/** Order within Viral trips, low first; null or absent sorts last. */
	rank?: number | null;
	/** The viral card's chip ("7.3M views on Instagram"). */
	stat?: string | null;
	/** The post a "Watch the video" link opens. */
	video?: string | null;
}

export interface BioLinksData {
	channel: BioLinkChannel;
	entries: BioLinkEntry[];
}

/** How many routes "Recent trips" shows; every route also sits in its country folder. */
export const NEWEST_COUNT = 7;

/** How many viral routes show before the "Show N more viral trips" button. */
export const VIRAL_SHOWN = 3;

/** The Instagram DM thread with @stippl.io (opens the Instagram app on a phone). */
export const DM_URL = "https://ig.me/m/stippl.io";

export const ONELINK_URL = "https://stippl.onelink.me/cgUq";
export const WEB_URL = "https://www.stippl.io";

/** Anything that is not a known channel is Instagram, matching what the server decides from `?from=`. */
export function normalizeChannel(value: unknown): BioLinkChannel {
	return value === "tiktok" ? "tiktok" : "instagram";
}

const utm = (channel: BioLinkChannel, campaign: string): string =>
	`utm_source=${encodeURIComponent(channel)}&utm_medium=social&utm_campaign=${encodeURIComponent(campaign)}`;

/** Both store buttons. Same URL: the OneLink opens the app if installed, else sends each visitor to their own store. */
export function appLink(channel: BioLinkChannel): string {
	return withCustomProductPage(`${ONELINK_URL}/?pid=linktree&c=links_${channel}`);
}

export function discoverLink(channel: BioLinkChannel): string {
	return `${WEB_URL}/discover?${utm(channel, "bio_links")}`;
}

export function esimLink(channel: BioLinkChannel): string {
	return `${WEB_URL}/esim?${utm(channel, "bio_links")}`;
}

export function homeLink(channel: BioLinkChannel): string {
	return `${WEB_URL}/?${utm(channel, "bio_links")}`;
}

/** The campaign names the route: `asked_<slug>` for a comment we answered, `video_<slug>` for one added for a post that did well. */
const campaignOf = (entry: Pick<BioLinkEntry, "slug" | "source">): string => `${entry.source === "video" ? "video_" : "asked_"}${entry.slug}`;

export function tripLink(entry: Pick<BioLinkEntry, "tripId" | "slug" | "source">, channel: BioLinkChannel): string {
	return `${WEB_URL}/routepreview/${encodeURIComponent(entry.tripId ?? "")}?${utm(channel, campaignOf(entry))}`;
}

/** A guide opens its public list; the campaign is named exactly like a route's. */
export function guideLink(entry: Pick<BioLinkEntry, "listId" | "slug" | "source">, channel: BioLinkChannel): string {
	return `${WEB_URL}/listpreview/${encodeURIComponent(entry.listId ?? "")}?${utm(channel, campaignOf(entry))}`;
}

/** Where a card goes: the guide when the entry has a `listId`, the trip otherwise. */
export function entryLink(entry: Pick<BioLinkEntry, "tripId" | "listId" | "slug" | "source">, channel: BioLinkChannel): string {
	return entry.listId ? guideLink(entry, channel) : tripLink(entry, channel);
}

/** What a tap on a card is for `links_click`: a guide or a trip. */
export function entryKind(entry: Pick<BioLinkEntry, "listId">): "trip" | "guide" {
	return entry.listId ? "guide" : "trip";
}

/** The DM card. Not tagged and not channel-specific: an Instagram DM is an Instagram DM. */
export function dmLink(): string {
	return DM_URL;
}

const isDeal = (entry: Pick<BioLinkEntry, "kind">): boolean => entry.kind === "deal";

/** The deals, in the order the loader delivered them (newest `live_from` first). They sit above everything else on the page. */
export function dealEntries(entries: BioLinkEntry[]): BioLinkEntry[] {
	return entries.filter(isDeal);
}

/**
 * "Recent trips": the newest `count` routes of an already newest-first list, viral ones left out (they
 * have their own section, and on their old dates they would sink to the bottom here anyway). A list of
 * only viral routes is an empty "Recent trips": it shows "The first route is on its way.".
 */
export function newestEntries(entries: BioLinkEntry[], count: number = NEWEST_COUNT): BioLinkEntry[] {
	return entries.filter((entry) => !isDeal(entry) && entry.viral !== true).slice(0, count);
}

/**
 * "Viral trips": the viral routes by `rank`, low first. A route with no rank sorts after every ranked
 * one, and ties (including all the unranked) keep the order they arrived in, which is newest first.
 * The tie-break is the arrival index rather than the sort's own stability, so it does not depend on
 * the engine.
 */
export function viralEntries(entries: BioLinkEntry[]): BioLinkEntry[] {
	const rankOf = (entry: BioLinkEntry): number => (typeof entry.rank === "number" ? entry.rank : Number.POSITIVE_INFINITY);
	return entries
		.map((entry, index) => ({ entry, index }))
		.filter(({ entry }) => !isDeal(entry) && entry.viral === true)
		.sort((a, b) => {
			const byRank = rankOf(a.entry) === rankOf(b.entry) ? 0 : rankOf(a.entry) < rankOf(b.entry) ? -1 : 1;
			return byRank || a.index - b.index;
		})
		.map(({ entry }) => entry);
}

/** The first `VIRAL_SHOWN` viral routes, or all of them once the visitor has unfolded the rest. */
export function visibleViral(viral: BioLinkEntry[], expanded: boolean): BioLinkEntry[] {
	return expanded ? viral : viral.slice(0, VIRAL_SHOWN);
}

/** How many viral routes the button keeps folded away. 0 means there is no button. */
export function hiddenViralCount(viral: BioLinkEntry[]): number {
	return Math.max(0, viral.length - VIRAL_SHOWN);
}

/** "Show 4 more viral trips" while folded, "Show fewer" once unfolded. */
export function viralToggleLabel(viral: BioLinkEntry[], expanded: boolean): string {
	if (expanded) return "Show fewer";
	const hidden = hiddenViralCount(viral);
	return `Show ${hidden} more viral ${hidden === 1 ? "trip" : "trips"}`;
}

export interface BioLinkFolder {
	country: string;
	name: string;
	flag: string;
	/** Newest first, the order the loader delivered them in. */
	entries: BioLinkEntry[];
}

/**
 * One folder per country, holding ALL of that country's routes, folders alphabetical by display name.
 *
 * The sort names its locale. A bare `localeCompare` uses the runtime's default locale, which on the
 * server (Node) and in a visitor's browser can differ, and two different orders are a hydration
 * mismatch that React reports and repairs by discarding the server HTML.
 */
export function groupByCountry(entries: BioLinkEntry[]): BioLinkFolder[] {
	const folders = new Map<string, BioLinkFolder>();
	for (const entry of entries) {
		if (isDeal(entry)) continue;
		const folder = folders.get(entry.country);
		if (folder) folder.entries.push(entry);
		else folders.set(entry.country, { country: entry.country, name: entry.countryName, flag: entry.flag, entries: [entry] });
	}
	return [...folders.values()].sort((a, b) => a.name.localeCompare(b.name, "en", { sensitivity: "base" }) || a.country.localeCompare(b.country, "en"));
}

/** "1 route" / "3 routes". */
/** A folder's count, routes and guides apart: "2 routes · 1 guide". A folder with only routes reads as before. */
export function routeCountLabel(entries: Pick<BioLinkEntry, "listId">[]): string {
	const guides = entries.filter((e) => e.listId).length;
	const routes = entries.length - guides;
	const part = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;
	return [routes ? part(routes, "route") : "", guides ? part(guides, "guide") : ""].filter(Boolean).join(" · ");
}

/** Each distinct photo credit once, in the order the routes appear. */
export function uniqueCredits(entries: BioLinkEntry[]): string[] {
	return [
		...new Set(
			entries
				.filter((entry) => !isDeal(entry))
				.map((entry) => entry.imageCredit)
				.filter(Boolean)
		),
	];
}
